/**
 * Root.tsx — top-level React tree wiring.
 *
 * Extracted from main.tsx so the react-refresh plugin sees a module that
 * exports only components (satisfies the react-refresh/only-export-components
 * rule which fails with --max-warnings 0 when component definitions are mixed
 * with side-effects or non-component exports in the same file).
 *
 * Provider stack (outermost first):
 *   LanguageContext    — exposes {language, toggleLanguage} to the whole tree
 *   ColorModeContext   — exposes {mode, toggleColorMode} to the whole tree
 *   ThemeProvider      — MUI 5 theme, switched by ColorModeContext.mode
 *   QueryClientProvider — @tanstack/react-query global cache
 *   App               — BrowserRouter + route table
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './app';
import {
  ColorModeContext,
  lightTheme,
  darkTheme,
  type ColorMode,
} from './theme/theme';
import {
  LanguageContext,
  type AppLanguage,
  LANGUAGE_STORAGE_KEY,
} from './core/i18n/index';
import i18next from './core/i18n/index';
import { AuthProvider } from './core/auth';
import { OnboardingProvider } from './core/onboarding';
import SiteGate from './core/gate/SiteGate';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time of 30s matches typical catalog data freshness; individual
      // hooks can override this when needed.
      staleTime: 30_000,
      retry: 1,
    },
  },
});

export default function Root() {
  // Initialise from localStorage so the preference survives page reload.
  const [mode, setMode] = useState<ColorMode>(() => {
    const stored = localStorage.getItem('itow-color-mode');
    return stored === 'dark' ? 'dark' : 'light';
  });

  const toggleColorMode = useCallback(() => {
    setMode((prev) => {
      const next: ColorMode = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('itow-color-mode', next);
      return next;
    });
  }, []);

  // Memoize the context value to avoid re-rendering every consumer on unrelated
  // parent re-renders.
  const colorModeValue = useMemo(
    () => ({ mode, toggleColorMode }),
    [mode, toggleColorMode]
  );

  // Language state — initialised from localStorage (same initial read that
  // i18next did at module load, kept in sync here for React state).
  const [language, setLanguage] = useState<AppLanguage>(() => {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === 'es-mx' ? 'es-mx' : 'en-us';
  });

  const toggleLanguage = useCallback(() => {
    setLanguage((prev) => {
      const next: AppLanguage = prev === 'en-us' ? 'es-mx' : 'en-us';
      localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
      // Sync the i18next instance so all useTranslation() calls re-render.
      void i18next.changeLanguage(next);
      return next;
    });
  }, []);

  const languageValue = useMemo(
    () => ({ language, toggleLanguage }),
    [language, toggleLanguage]
  );

  // Keep `<html lang="...">` in sync with the active locale so screen readers
  // pronounce content correctly. Maps app locale codes to BCP-47 (en-us → en, es-mx → es-MX).
  useEffect(() => {
    document.documentElement.lang = language === 'es-mx' ? 'es-MX' : 'en';
  }, [language]);

  return (
    <AuthProvider>
      <LanguageContext.Provider value={languageValue}>
        <ColorModeContext.Provider value={colorModeValue}>
          <ThemeProvider theme={mode === 'light' ? lightTheme : darkTheme}>
            {/* App-wide, not only inside AppShell.
                `/login` and `/forgot-password` render outside the shell, so
                with CssBaseline scoped there the browser's default 8px body
                margin survived on exactly the two pages that bleed a panel to
                the viewport edge. Idempotent — AppShell may keep its own. */}
            <CssBaseline />
            <SiteGate>
              <QueryClientProvider client={queryClient}>
                {/* Inside AuthProvider, because it asks the server a question
                    only a signed-in user can answer; outside the router, so the
                    answer is fetched once per session rather than on every
                    navigation the gate inspects. */}
                <OnboardingProvider>
                  <App />
                </OnboardingProvider>
              </QueryClientProvider>
            </SiteGate>
          </ThemeProvider>
        </ColorModeContext.Provider>
      </LanguageContext.Provider>
    </AuthProvider>
  );
}
